import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useDeploymentViewModel } from '../ViewModel/DeploymentViewModel';
import { Site } from '../Model/Site';
import DeploymentStatusTracker from '../Components/DeploymentStatusTracker';
import { SSLConfigModal } from '../Components/SslConfigModel';
import axiosInstance from '../Core/RestHelper';

const formatDate = (dateStr: string | undefined) =>
  dateStr ? new Date(dateStr).toLocaleString() : 'N/A';

const card: React.CSSProperties = {
  background: '#111116',
  border: '1px solid #26262f',
  borderRadius: '10px',
  padding: '16px 18px',
};

const btn = (variant: 'primary' | 'secondary' | 'danger' | 'warn' | 'ghost' = 'secondary'): React.CSSProperties => ({
  display: 'inline-flex', alignItems: 'center', gap: '6px',
  padding: '6px 12px', borderRadius: '6px', fontSize: '12.5px',
  fontWeight: 500, cursor: 'pointer', border: 'none',
  fontFamily: "'Inter',sans-serif", transition: 'all .15s', whiteSpace: 'nowrap' as const,
  ...(variant === 'primary'
    ? { background: '#6366f1', color: '#fff' }
    : variant === 'danger'
    ? { background: 'rgba(244,63,94,.1)', color: '#f43f5e', border: '1px solid rgba(244,63,94,.2)' }
    : variant === 'warn'
    ? { background: 'rgba(234,179,8,.1)', color: '#eab308', border: '1px solid rgba(234,179,8,.2)' }
    : variant === 'ghost'
    ? { background: 'transparent', color: '#8e8ea8', border: '1px solid #26262f' }
    : { background: '#17171e', color: '#eeeef5', border: '1px solid #32323d' }),
});

const statusStyle = (status: string): React.CSSProperties => {
  const s = status.toLowerCase();
  if (['completed', 'active', 'running'].includes(s))
    return { background: 'rgba(34,197,94,.1)', color: '#22c55e', border: '1px solid rgba(34,197,94,.2)' };
  if (['in_progress', 'processing', 'queued'].includes(s))
    return { background: 'rgba(234,179,8,.1)', color: '#eab308', border: '1px solid rgba(234,179,8,.2)' };
  return { background: 'rgba(244,63,94,.1)', color: '#f43f5e', border: '1px solid rgba(244,63,94,.2)' };
};

const parseDeploymentResult = (raw: string | undefined) => {
  if (!raw) return null;
  const parts = raw.split(',').map(s => s.trim()).filter(Boolean);
  return {
    summary: parts[0] ?? '',
    rootCause: parts[1] ?? '',
    suggestedFix: parts[2] ?? '',
  };
};

const dotColor = (status: string) => {
  const s = status.toLowerCase();
  if (['completed', 'active', 'running'].includes(s)) return '#22c55e';
  if (['in_progress', 'processing', 'queued'].includes(s)) return '#eab308';
  return '#f43f5e';
};

const DetailCard: React.FC<{
  title: string;
  items: { label: string; value: React.ReactNode }[];
}> = ({ title, items }) => (
  <div style={card}>
    <div style={{
      fontSize: '10px', fontWeight: 700, textTransform: 'uppercase' as const,
      letterSpacing: '.08em', color: '#52525f', marginBottom: '12px',
    }}>
      {title}
    </div>
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
      {items.map((item, idx) => (
        <div
          key={idx}
          style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
            padding: '8px 0',
            borderBottom: idx < items.length - 1 ? '1px solid #26262f' : 'none',
          }}
        >
          <span style={{ fontSize: '11.5px', color: '#52525f', flexShrink: 0, marginRight: '12px' }}>
            {item.label}
          </span>
          <span style={{ fontSize: '12.5px', color: '#eeeef5', fontWeight: 500, textAlign: 'right' as const, wordBreak: 'break-all' as const }}>
            {item.value}
          </span>
        </div>
      ))}
    </div>
  </div>
);

export const SiteDetailView: React.FC = () => {
  const { getDeploymentDetails, triggerDeployment, getHostedLink } = useDeploymentViewModel();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const trackerRef = useRef<HTMLDivElement>(null);
  const [showResultPopup, setShowResultPopup] = useState<boolean>(false);
  const resultPopupRef = useRef<HTMLDivElement>(null);

  const [instanceId, setInstanceId] = useState<string>();
  const [deploymentData, setDeploymentData] = useState<Site | undefined>();
  const [monitorDeployment, setMonitorDeployment] = useState<boolean>(false);
  const [showSSLModal, setShowSSLModal] = useState<boolean>(false);
  const [isNewDeployment, setIsNewDeployment] = useState<boolean>(false);
  const [isReportingIssue, setIsReportingIssue] = useState<boolean>(false);
  const [reportStatus, setReportStatus] = useState<string | null>(null);
  const [showReportButton, setShowReportButton] = useState<boolean>(false);
  const [hostedLink, setHostedLink] = useState<string>();
  const [isSyncing, setIsSyncing] = useState<boolean>(false);
  const [notificationModal, setNotificationModal] = useState<{
    isOpen: boolean; type: 'success' | 'error'; title: string; message: string;
  }>({ isOpen: false, type: 'success', title: '', message: '' });

  const showNotification = (type: 'success' | 'error', title: string, message: string) =>
    setNotificationModal({ isOpen: true, type, title, message });
  const hideNotification = () =>
    setNotificationModal({ isOpen: false, type: 'success', title: '', message: '' });

  const scrollToTracker = () => {
    setTimeout(() => {
      trackerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  const handleTriggerDeployment = async () => {
    if (id) {
      const deploymentStatus = await triggerDeployment(id);
      if (deploymentStatus?.success) {
        setIsNewDeployment(true);
        setMonitorDeployment(true);
        scrollToTracker();
      }
    }
  };

  const showHostedLink = async () => {
    if (id) {
      const link = await getHostedLink(id);
      if (link?.success) setHostedLink(link.data);
    }
  };

  const checkReportStatus = async (deploymentId: string) => {
    try {
      const response = await axiosInstance.get(`deploy/get-reports-status/${deploymentId}`);
      if (response.data.success) {
        if (response.data.message === 'No data found') {
          setShowReportButton(true);
          setReportStatus(null);
        } else {
          const status = response.data.message.toLowerCase();
          if (status === 'resolved') {
            setShowReportButton(false);
            setReportStatus(null);
          } else {
            setShowReportButton(false);
            setReportStatus(response.data.message);
          }
        }
      }
    } catch {
      setShowReportButton(true);
      setReportStatus(null);
    }
  };

  const handleReportFailedIssue = async () => {
    if (!id) return;
    setIsReportingIssue(true);
    try {
      const response = await axiosInstance.post(`deploy/deployments/report-failed/${id}`);
      if (response.data.success) {
        showNotification('success', 'Issue Reported', 'Your failed deployment issue has been reported. Our team will review it shortly.');
        await checkReportStatus(id);
      } else {
        showNotification('error', 'Report Failed', response.data.message || 'Failed to report issue. Please try again.');
      }
    } catch (error: any) {
      showNotification('error', 'Report Failed', error.response?.data?.message || 'Failed to report issue. Please check your connection.');
    } finally {
      setIsReportingIssue(false);
    }
  };

  const handleViewLogs = () => {
    const isInProgress = ['in_progress', 'processing', 'queued'].includes(deploymentData?.status.toLowerCase() || '');
    setIsNewDeployment(isInProgress);
    setMonitorDeployment(true);
    scrollToTracker();
  };

  const handleCloseMonitoring = () => {
    setMonitorDeployment(false);
    setIsNewDeployment(false);
  };

  const handleSync = async () => {
    if (!id) return;
    setIsSyncing(true);
    const res = await getDeploymentDetails(id);
    if (res?.success && res?.data) {
      setDeploymentData(res.data);
      if (res.data.is_cicd_trigger_on && !monitorDeployment) {
        setIsNewDeployment(true);
        setMonitorDeployment(true);
        scrollToTracker();
      }
    }
    setIsSyncing(false);
  };

  // Single on-load effect: fetches data and handles all auto-open conditions
  useEffect(() => {
    const fetchData = async () => {
      if (id) {
        const res = await getDeploymentDetails(id);
        if (res?.success && res?.data) {
          setDeploymentData(res.data);
          setInstanceId(res.data.instance_meta?.instance_id);
          if (res.data.status.toLowerCase() === 'failed') await checkReportStatus(id);

          const isInProgress = ['in_progress', 'processing', 'queued'].includes(res.data.status.toLowerCase());
          const fromDeploy = searchParams.get('fromDeploy');

          if (isInProgress || fromDeploy === 'true' || res.data.is_cicd_trigger_on) {
            setIsNewDeployment(isInProgress || res.data.is_cicd_trigger_on);
            setMonitorDeployment(true);
            scrollToTracker();
          }
        }
      }
    };
    fetchData();
    showHostedLink();
  }, [id]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (resultPopupRef.current && !resultPopupRef.current.contains(e.target as Node)) {
        setShowResultPopup(false);
      }
    };
    if (showResultPopup) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showResultPopup]);

  if (!deploymentData) {
    return (
      <div style={{ background: '#09090e', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: '12px' }}>
        <div style={{ width: '28px', height: '28px', borderRadius: '50%', border: '2px solid #26262f', borderTopColor: '#6366f1', animation: 'spin .8s linear infinite' }}/>
        <span style={{ fontSize: '13px', color: '#52525f' }}>Loading deployment details…</span>
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      </div>
    );
  }

  const isInProgress = ['in_progress', 'processing', 'queued'].includes(deploymentData.status.toLowerCase());

  return (
    <div style={{ background: '#09090e', minHeight: '100vh', fontFamily: "'Inter',sans-serif" }}>

      {/* Topbar */}
      <div style={{
        height: '50px', background: '#111116', borderBottom: '1px solid #26262f',
        display: 'flex', alignItems: 'center', padding: '0 20px', gap: '10px',
        position: 'sticky' as const, top: 0, zIndex: 50,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12.5px', color: '#8e8ea8' }}>
          <span
            style={{ color: '#818cf8', cursor: 'pointer' }}
            onClick={() => navigate('/sites')}
          >
            Deployments
          </span>
          <span style={{ color: '#52525f' }}>›</span>
          <span style={{ color: '#eeeef5', fontWeight: 500 }}>{deploymentData.app_name}</span>
        </div>

        {/* Action buttons */}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '7px', flexWrap: 'wrap' as const }}>

          {/* Sync button */}
          <button
            style={{ ...btn('ghost'), opacity: isSyncing ? 0.6 : 1, cursor: isSyncing ? 'not-allowed' : 'pointer' }}
            onClick={handleSync}
            disabled={isSyncing}
          >
            <svg
              style={isSyncing ? { animation: 'spin .8s linear infinite' } : undefined}
              width="13" height="13" viewBox="0 0 13 13" fill="none" stroke="currentColor" strokeWidth="1.5"
            >
              <path d="M1 6.5A5.5 5.5 0 006.5 12M12 6.5A5.5 5.5 0 006.5 1"/>
              <path d="M10.5 1.5l1.5 2-2 1"/>
            </svg>
            {isSyncing ? 'Syncing…' : 'Sync'}
          </button>

          {/* Monitor button — only show when not already monitoring */}
          {!monitorDeployment && (
            <button
              style={btn('secondary')}
              onClick={() => { setMonitorDeployment(true); scrollToTracker(); }}
            >
              <svg width="13" height="13" viewBox="0 0 13 13" fill="none" stroke="currentColor" strokeWidth="1.5">
                <rect x="1.5" y="2" width="10" height="9" rx="1.5"/>
                <path d="M3.5 5h6M3.5 7.5h4"/>
              </svg>
              Monitor
            </button>
          )}

          <button style={btn('secondary')} onClick={handleTriggerDeployment}>
            <svg width="13" height="13" viewBox="0 0 13 13" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M1 6.5A5.5 5.5 0 006.5 12M12 6.5A5.5 5.5 0 006.5 1M10 3.5l2-2"/>
            </svg>
            Redeploy
          </button>

          {deploymentData.status.toLowerCase() === 'completed' && (
            <button style={btn('primary')} onClick={() => setShowSSLModal(true)}>
              <svg width="13" height="13" viewBox="0 0 13 13" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M6.5 1L2 3v4c0 2.5 2 4.5 4.5 5 2.5-.5 4.5-2.5 4.5-5V3L6.5 1z"/>
              </svg>
              Configure SSL
            </button>
          )}

          {deploymentData.status.toLowerCase() === 'failed' && (
            <>
              {reportStatus && (
                <div style={{ ...btn('warn'), cursor: 'default' }}>
                  <svg width="13" height="13" viewBox="0 0 13 13" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <circle cx="6.5" cy="6.5" r="5.5"/><path d="M6.5 3.5v3M6.5 9v.5"/>
                  </svg>
                  Issue Status: {reportStatus}
                </div>
              )}
              {showReportButton && (
                <button
                  style={{ ...btn('danger'), opacity: isReportingIssue ? 0.6 : 1, cursor: isReportingIssue ? 'not-allowed' : 'pointer' }}
                  onClick={handleReportFailedIssue}
                  disabled={isReportingIssue}
                >
                  {isReportingIssue ? (
                    <svg style={{ animation: 'spin .8s linear infinite' }} width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4"/>
                    </svg>
                  ) : (
                    <svg width="13" height="13" viewBox="0 0 13 13" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d="M6.5 1.5l5 9.5H1.5l5-9.5zM6.5 5v3M6.5 9.5v.5"/>
                    </svg>
                  )}
                  {isReportingIssue ? 'Reporting…' : 'Report Issue'}
                </button>
              )}
            </>
          )}

          {deploymentData.deployment_result && (() => {
              const parsed = parseDeploymentResult(deploymentData.deployment_result);
              const isSuccess = deploymentData.status.toLowerCase() === 'completed';
              return (
                <div style={{ position: 'relative' }} ref={resultPopupRef}>
                  <button
                    style={{
                      ...btn('secondary'),
                      borderColor: isSuccess ? 'rgba(34,197,94,.25)' : 'rgba(244,63,94,.25)',
                      background: isSuccess ? 'rgba(34,197,94,.08)' : 'rgba(244,63,94,.08)',
                      color: isSuccess ? '#22c55e' : '#f43f5e',
                    }}
                    onClick={() => setShowResultPopup(v => !v)}
                  >
                    <svg width="13" height="13" viewBox="0 0 13 13" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <circle cx="6.5" cy="6.5" r="5.5"/><path d="M6.5 5.5v4M6.5 4v-.5"/>
                    </svg>
                    Deployment Result
                  </button>

                  {showResultPopup && parsed && (
                    <div style={{
                      position: 'absolute', top: 'calc(100% + 8px)', right: 0,
                      background: '#111116', border: '1px solid #26262f',
                      borderRadius: '12px', padding: '20px', width: '360px', zIndex: 100,
                      boxShadow: '0 16px 48px rgba(0,0,0,.6)',
                      animation: 'fadeIn .15s ease-out',
                    }}>
                      {/* Header */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' }}>
                        <div style={{
                          width: '32px', height: '32px', borderRadius: '8px', flexShrink: 0,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          background: isSuccess ? 'rgba(34,197,94,.1)' : 'rgba(244,63,94,.1)',
                          border: `1px solid ${isSuccess ? 'rgba(34,197,94,.2)' : 'rgba(244,63,94,.2)'}`,
                        }}>
                          {isSuccess
                            ? <svg width="15" height="15" viewBox="0 0 15 15" fill="none" stroke="#22c55e" strokeWidth="1.5"><path d="M2 7.5l4.5 4.5 6.5-7"/></svg>
                            : <svg width="15" height="15" viewBox="0 0 15 15" fill="none" stroke="#f43f5e" strokeWidth="1.5"><path d="M2 2l11 11M13 2L2 13"/></svg>
                          }
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: '13px', fontWeight: 600, color: '#eeeef5' }}>
                            {isSuccess ? 'Deployment Successful' : 'Deployment Failed'}
                          </div>
                          <div style={{ fontSize: '11px', color: '#52525f', marginTop: '1px' }}>Latest result</div>
                        </div>
                        <button
                          onClick={() => setShowResultPopup(false)}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#52525f', padding: '2px', display: 'flex' }}
                        >
                          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5">
                            <path d="M2 2l10 10M12 2L2 12"/>
                          </svg>
                        </button>
                      </div>

                      {/* Sections */}
                      {[
                        { label: 'Summary', value: parsed.summary },
                        { label: 'Root Cause', value: parsed.rootCause },
                        { label: 'Suggested Fix', value: parsed.suggestedFix },
                      ].map(({ label, value }) => (
                        <div key={label} style={{ background: '#17171e', borderRadius: '8px', padding: '10px 12px', marginBottom: '6px' }}>
                          <div style={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase' as const, letterSpacing: '.08em', color: '#52525f', marginBottom: '5px' }}>
                            {label}
                          </div>
                          <div style={{ fontSize: '12.5px', color: value ? '#eeeef5' : '#52525f', lineHeight: 1.5, fontStyle: value ? 'normal' : 'italic' }}>
                            {value || 'None'}
                          </div>
                        </div>
                      ))}

                      {/* View / Hide Logs */}
                      <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px solid #26262f' }}>
                        {!isInProgress && monitorDeployment ? (
                          <button
                            style={{ ...btn('ghost'), width: '100%', justifyContent: 'center' }}
                            onClick={() => { handleCloseMonitoring(); setShowResultPopup(false); }}
                          >
                            <svg width="13" height="13" viewBox="0 0 13 13" fill="none" stroke="currentColor" strokeWidth="1.5">
                              <path d="M2 2l9 9M11 2L2 11"/>
                            </svg>
                            Hide Logs
                          </button>
                        ) : (
                          <button
                            style={{ ...btn('secondary'), width: '100%', justifyContent: 'center' }}
                            onClick={() => { handleViewLogs(); setShowResultPopup(false); }}
                          >
                            <svg width="13" height="13" viewBox="0 0 13 13" fill="none" stroke="currentColor" strokeWidth="1.5">
                              <rect x="1.5" y="2" width="10" height="9" rx="1.5"/>
                              <path d="M3.5 5h6M3.5 7.5h4"/>
                            </svg>
                            View Logs
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}

          <button style={btn('ghost')} onClick={() => navigate('/sites')}>
            <svg width="13" height="13" viewBox="0 0 13 13" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M10.5 6.5h-8M6 3.5L2.5 6.5 6 10"/>
            </svg>
            Back
          </button>
        </div>
      </div>

      <div style={{ padding: '20px 24px' }}>

        {/* App header card */}
        <div style={{
          ...card,
          display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
          gap: '16px', marginBottom: '18px',
          background: 'linear-gradient(135deg,rgba(99,102,241,.06),rgba(168,85,247,.04))',
          border: '1px solid rgba(99,102,241,.15)',
        }}>
          <div>
            <div style={{ fontFamily: "'Syne',sans-serif", fontSize: '18px', fontWeight: 700, color: '#eeeef5', display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
              {deploymentData.app_name}
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: '4px',
                padding: '2px 8px', borderRadius: '20px', fontSize: '10.5px', fontWeight: 600,
                ...statusStyle(deploymentData.status),
              }}>
                <span style={{ width: '5px', height: '5px', borderRadius: '50%', background: dotColor(deploymentData.status), flexShrink: 0 }}/>
                {deploymentData.status}
              </span>
            </div>
            <div style={{ fontSize: '12px', color: '#8e8ea8', fontFamily: "'IBM Plex Mono',monospace" }}>
              {deploymentData.repo_name} · {deploymentData.branch}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', fontSize: '10px', fontWeight: 700, padding: '2px 6px', borderRadius: '3px', background: 'rgba(255,153,0,.12)', color: '#ff9900' }}>
              {deploymentData.selected_cloud || 'AWS'}
            </span>
            <span style={{ display: 'inline-flex', padding: '2px 7px', borderRadius: '20px', fontSize: '10.5px', fontWeight: 600, background: '#1e1e27', color: '#8e8ea8', border: '1px solid #26262f' }}>
              {deploymentData.service_selected || 'EC2'}
            </span>
          </div>
        </div>

        {/* Detail cards grid */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '18px' }}>
          <DetailCard
            title="General Info"
            items={[
              { label: 'App Name', value: deploymentData.app_name },
              { label: 'Repository', value: <span style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: '11.5px' }}>{deploymentData.repo_name}</span> },
              { label: 'Branch', value: <span style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: '11.5px', color: '#818cf8' }}>{deploymentData.branch}</span> },
              { label: 'User ID', value: <span style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: '11px', color: '#8e8ea8' }}>{deploymentData.user_id}</span> },
            ]}
          />

          <DetailCard
            title="Configuration"
            items={[
              { label: 'Cloud Provider', value: <span style={{ fontSize: '10px', fontWeight: 700, padding: '2px 6px', borderRadius: '3px', background: 'rgba(255,153,0,.12)', color: '#ff9900' }}>{deploymentData.selected_cloud}</span> },
              { label: 'Service', value: <span style={{ display: 'inline-flex', padding: '2px 7px', borderRadius: '20px', fontSize: '10.5px', fontWeight: 600, background: '#1e1e27', color: '#8e8ea8', border: '1px solid #26262f' }}>{deploymentData.service_selected}</span> },
              { label: 'CI/CD', value: deploymentData.ci_cd
                ? <span style={{ display: 'inline-flex', padding: '2px 7px', borderRadius: '20px', fontSize: '10.5px', fontWeight: 600, background: 'rgba(99,102,241,.14)', color: '#818cf8' }}>Enabled</span>
                : <span style={{ color: '#52525f', fontSize: '12.5px' }}>Disabled</span>
              },
              { label: 'Monitoring', value: deploymentData.monitoring
                ? <span style={{ color: '#22c55e', fontSize: '12.5px' }}>Enabled</span>
                : <span style={{ color: '#52525f', fontSize: '12.5px' }}>Disabled</span>
              },
              { label: 'Analyze Options', value: deploymentData.analyze_options || '—' },
            ]}
          />

          <DetailCard
            title="Timestamps"
            items={[
              { label: 'Created On', value: formatDate(deploymentData.created_on) },
              { label: 'Modified At', value: formatDate(deploymentData.modified_at) },
            ]}
          />

          <DetailCard
            title="Environment Variables"
            items={[
              { label: '', value: (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12.5px', color: '#8e8ea8' }}>
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="#52525f" strokeWidth="1.5">
                    <rect x="1" y="3" width="12" height="9" rx="1.5"/>
                    <path d="M4 3V2h6v1"/>
                  </svg>
                  Managed via AWS Secrets Manager
                </div>
              )},
            ]}
          />

          {deploymentData.instance_meta && (
            <DetailCard
              title="Instance Meta"
              items={[
                { label: 'Instance ID', value: <span style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: '11px', color: '#818cf8' }}>{deploymentData.instance_meta.instance_id || 'N/A'}</span> },
                { label: 'Instance Type', value: deploymentData.instance_meta.instance_type || 'N/A' },
                { label: 'Availability Zone', value: deploymentData.instance_meta.availability_zone || 'N/A' },
                { label: 'State', value: deploymentData.instance_meta.state ? (
                  <span style={{ ...statusStyle(deploymentData.instance_meta.state), display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '2px 7px', borderRadius: '20px', fontSize: '10.5px', fontWeight: 600 }}>
                    {deploymentData.instance_meta.state}
                  </span>
                ) : 'N/A' },
                { label: 'Launch Time', value: formatDate(deploymentData.instance_meta.launch_time) },
                { label: 'Domain / Link', value: deploymentData.domain_name ? (
                  <a href={`https://${deploymentData.domain_name}`} target="_blank" rel="noopener noreferrer" style={{ color: '#818cf8', fontSize: '12.5px', textDecoration: 'underline' }}>
                    https://{deploymentData.domain_name}
                  </a>
                ) : hostedLink ? (
                  <a href={hostedLink} target="_blank" rel="noopener noreferrer" style={{ color: '#818cf8', fontSize: '12.5px', textDecoration: 'underline', wordBreak: 'break-all' as const }}>
                    {hostedLink}
                  </a>
                ) : <span style={{ color: '#52525f' }}>N/A</span> },
              ]}
            />
          )}
        </div>

        {/* Deployment tracker */}
        {monitorDeployment && (
          <div ref={trackerRef} style={{ scrollMarginTop: '70px' }}>
            <DeploymentStatusTracker
              deploymentId={id ?? '0'}
              isOpen={monitorDeployment}
              onClose={handleCloseMonitoring}
              isNewDeployment={isNewDeployment}
            />
          </div>
        )}
      </div>

      {/* Notification Modal */}
      {notificationModal.isOpen && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200 }}>
          <div style={{ background: '#111116', border: '1px solid #32323d', borderRadius: '14px', padding: '24px', width: '400px', maxWidth: '95vw', boxShadow: '0 24px 60px rgba(0,0,0,.7)', animation: 'fadeIn .2s ease-out' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
              <div style={{
                width: '36px', height: '36px', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: notificationModal.type === 'success' ? 'rgba(34,197,94,.1)' : 'rgba(244,63,94,.1)',
                border: `1px solid ${notificationModal.type === 'success' ? 'rgba(34,197,94,.2)' : 'rgba(244,63,94,.2)'}`,
              }}>
                {notificationModal.type === 'success' ? (
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="#22c55e" strokeWidth="1.5"><path d="M2 8l5 5 7-7"/></svg>
                ) : (
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="#f43f5e" strokeWidth="1.5"><circle cx="8" cy="8" r="6.5"/><path d="M8 5v3M8 10v.5"/></svg>
                )}
              </div>
              <div style={{ fontFamily: "'Syne',sans-serif", fontSize: '15px', fontWeight: 700, color: '#eeeef5' }}>
                {notificationModal.title}
              </div>
            </div>
            <p style={{ fontSize: '13px', color: '#8e8ea8', marginBottom: '20px' }}>
              {notificationModal.message}
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button
                onClick={hideNotification}
                style={{
                  padding: '7px 16px', borderRadius: '6px', fontSize: '13px', fontWeight: 500,
                  border: 'none', cursor: 'pointer',
                  background: notificationModal.type === 'success' ? '#22c55e' : '#f43f5e',
                  color: '#fff',
                }}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      <SSLConfigModal
        isOpen={showSSLModal}
        onClose={() => setShowSSLModal(false)}
        deploymentId={id || ''}
        instanceId={instanceId || ''}
        publicIp={deploymentData.instance_meta?.public_ip}
      />

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes fadeIn { from { opacity: 0; transform: scale(0.95); } to { opacity: 1; transform: scale(1); } }
      `}</style>
    </div>
  );
};